import graphql__c2d67e368d74be422899 from "./__generated__/useSimilarAnswers_answer.graphql.js";import { useState, useCallback, useMemo, useRef } from "react";
import { useFragment, graphql } from "react-relay";

import { countWords } from "../Utils/countWords";
import { useDebouncedAnswerQuery } from "./useDebouncedAnswerQuery";
import { useSimilarAnswersFetch, MIN_WORD_COUNT } from "./useSimilarAnswersFetch";
import { isAuthorized } from "../Utils/authorizations";

// Central coordinator for the similar-answers feature. Reads the Relay fragment,
// wires the fetch and debounce sub-hooks together, and owns the review lifecycle
// (dismissed / reviewed states, ignored IDs accumulation).
export const useSimilarAnswers = inputAnswer => {
  const answer = useFragment(useSimilarAnswers_answer, inputAnswer);
  const { id: answerId, formField } = answer;
  const { id: formFieldId, authorizations } = formField;

  const displaySimilarContributionsSuggestions = isAuthorized(
    authorizations?.displaySimilarContributionsSuggestions
  );

  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const [suggestionsReviewed, setSuggestionsReviewed] = useState(false);
  const ignoredAnswerIdsRef = useRef([]);

  const { fetchSimilarAnswers, similarAnswers, isLoading } = useSimilarAnswersFetch(
    formFieldId,
    answerId,
    displaySimilarContributionsSuggestions
  );

  // Pre-bind ignoredAnswerIds at call time. A ref cannot be closed over safely, so we
  // wrap in useCallback to give useDebouncedAnswerQuery a stable (text) => void
  // signature.
  const fetchSimilarAnswersWithIgnoredIds = useCallback(
    text => fetchSimilarAnswers(text, ignoredAnswerIdsRef.current),
    [fetchSimilarAnswers]
  );

  const { handleValueChange, currentValueRef, resetLastQueryCount } =
    useDebouncedAnswerQuery(fetchSimilarAnswersWithIgnoredIds, {
      enabled: !suggestionsDismissed && !suggestionsReviewed
    });

  const dismissSuggestions = useCallback(() => {
    setSuggestionsDismissed(true);
  }, []);

  // Appends the IDs of all currently shown answers to the ignore list so they are
  // excluded from the next fetch (seek-more or reset). Called before any re-fetch.
  const addSimilarAnswersToIgnoredList = useCallback(() => {
    const newIgnoredIds = similarAnswers.map(a => a.id);
    ignoredAnswerIdsRef.current = [...ignoredAnswerIdsRef.current, ...newIgnoredIds];
  }, [similarAnswers]);

  const completeSuggestionsReview = useCallback(() => {
    addSimilarAnswersToIgnoredList();
    setSuggestionsReviewed(true);
  }, [addSimilarAnswersToIgnoredList]);

  const resetSuggestionsReview = useCallback(() => {
    setSuggestionsDismissed(false);
    setSuggestionsReviewed(false);
    resetLastQueryCount();
    fetchSimilarAnswers(currentValueRef.current, ignoredAnswerIdsRef.current);
  }, [fetchSimilarAnswers, currentValueRef, resetLastQueryCount]);

  const seekMoreSuggestions = useCallback(() => {
    addSimilarAnswersToIgnoredList();
    setSuggestionsReviewed(false);
    resetLastQueryCount();
    fetchSimilarAnswers(currentValueRef.current, ignoredAnswerIdsRef.current);
  }, [
    addSimilarAnswersToIgnoredList,
    fetchSimilarAnswers,
    currentValueRef,
    resetLastQueryCount
  ]);

  // Reads from a ref rather than state so this computation does not require a separate
  // state variable. The value is always fresh at render time: the component re-renders
  // naturally whenever similarAnswers or isLoading change, at which point this is
  // recomputed against the latest currentValueRef.
  const shouldShowSuggestions =
    displaySimilarContributionsSuggestions &&
    countWords(currentValueRef.current) >= MIN_WORD_COUNT;

  // Extract contribution Relay IDs from the similar answers response for the review
  // funnel, which loads full contribution data through its own dedicated queries.
  const contributionIds = useMemo(
    () => similarAnswers.map(a => a.formAnswer?.id).filter(Boolean),
    [similarAnswers]
  );

  return {
    contributionIds,
    isLoading,
    handleValueChange,
    shouldShowSuggestions,
    dismissSuggestions,
    suggestionsDismissed,
    completeSuggestionsReview,
    suggestionsReviewed,
    resetSuggestionsReview,
    seekMoreSuggestions
  };
};

const useSimilarAnswers_answer = graphql__c2d67e368d74be422899;
